import { clamp, yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Finds the cart icon a flying thingy should land on.
 *
 * The menu drawer renders its own copy of the header actions, and it comes first in the
 * document, so the first match in the DOM is the drawer's icon rather than the header's.
 * A closed drawer is parked off the left edge with `visibility: hidden`, so that copy still
 * measures a full-size box and can't be told apart by geometry — it flies to the hamburger.
 *
 * @returns {Element | null} The cart icon sitting in the header itself.
 */
export function getCartIcon() {
  const icons = document.querySelectorAll('.header-actions__cart-icon');

  /** @type {Element | null} */
  let fallback = null;

  for (const icon of icons) {
    if (icon.closest('header-drawer, .menu-drawer')) continue;

    fallback ??= icon;

    const { width, height } = icon.getBoundingClientRect();
    if (width > 0 && height > 0) return icon;
  }

  return fallback;
}

class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  /** @type {boolean} */
  #started = false;

  connectedCallback() {
    super.connectedCallback();
    this.#raiseAboveModals();

    /** @type {DOMRectReadOnly | null} */
    let sourceRect = null;
    /** @type {DOMRectReadOnly | null} */
    let destinationRect = null;

    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === this.source) {
          sourceRect = entry.boundingClientRect;
        } else if (entry.target === this.destination) {
          destinationRect = entry.boundingClientRect;
        }
      }

      if (!sourceRect || !destinationRect) return;

      clearTimeout(measurementDeadline);
      intersectionObserver.disconnect();
      this.#start(sourceRect, destinationRect);
    });
    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);

    const measurementDeadline = setTimeout(() => {
      intersectionObserver.disconnect();

      if (!this.isConnected) return;

      this.#start(this.source.getBoundingClientRect(), this.destination.getBoundingClientRect());
    }, 100);
  }

  /**
   * Runs the animation once, whichever measurement got there first.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #start(sourceRect, destinationRect) {
    if (this.#started) return;

    this.#started = true;
    this.#animate(sourceRect, destinationRect);
  }

  #raiseAboveModals() {
    if (typeof this.showPopover !== 'function') return;

    try {
      if (!document.querySelector('dialog:modal')) return;

      this.setAttribute('popover', 'manual');
      this.showPopover();
    } catch {
      this.removeAttribute('popover');
    }
  }

  /**
   * Animates the flying thingy along the bezier curve.
   * @param {DOMRectReadOnly} sourceRect - The bounding client rect of the source.
   * @param {DOMRectReadOnly} destinationRect - The bounding client rect of the destination.
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const edge = 20;
    const endPoint = {
      x: clamp(destinationRect.left + destinationRect.width / 2, edge, window.innerWidth - edge),
      y: clamp(destinationRect.top + destinationRect.height / 2, edge, window.innerHeight - edge),
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${sourceRect.width}px`);
      this.style.setProperty('--height', `${sourceRect.height}px`);
    }
    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await yieldToMainThread();

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));
    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
